import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';

const OpeningHours = ({ className = "", compact = false }) => {
  const [openingHours, setOpeningHours] = useState({
    hours: "8:30am - 5:30pm",
    closed_on_tuesdays: false,
    holiday_hours_notice: false
  });
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchOpeningHours();
  }, []);

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opening-hours`);
      if (response.ok) {
        const data = await response.json();
        setOpeningHours(data);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasNotices = openingHours.closed_on_tuesdays || openingHours.holiday_hours_notice;

  if (loading) {
    return <span className={className}>8:30am - 5:30pm</span>;
  }

  // Compact mode for header - just hours with indicator
  if (compact) {
    return (
      <div className="relative inline-block" data-testid="opening-hours">
        <div className="flex items-center gap-2">
          <span className={className}>{openingHours.hours}</span>
          {hasNotices && (
            <button
              className="px-2 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-full animate-pulse hover:animate-none transition-all cursor-pointer whitespace-nowrap"
              onMouseEnter={() => setShowDetails(true)}
              onMouseLeave={() => setShowDetails(false)}
              onClick={() => setShowDetails(!showDetails)}
              data-testid="hours-notice-btn"
            >
              Opening Hours Notice
            </button>
          )}
        </div>

        {/* Popover with details */}
        {showDetails && hasNotices && (
          <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 text-left">
            <div className="space-y-3">
              <div className="font-semibold text-gray-900 border-b pb-2">
                Opening Hours
              </div>
              <div className="text-sm text-gray-700">
                {openingHours.hours}
              </div>
              
              {openingHours.closed_on_tuesdays && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-sm" data-testid="closed-tuesdays">
                  <div className="font-semibold text-red-900">⚠️ Closed on Tuesdays</div>
                </div>
              )}
              
              {openingHours.holiday_hours_notice && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm" data-testid="holiday-notice">
                  <div className="font-semibold text-amber-900 mb-1">⚠️ Seasonal Closure</div>
                  <div className="text-amber-800">
                    Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full mode for footer and other places - show everything
  return (
    <div className={className} data-testid="opening-hours">
      <div className="font-medium">{openingHours.hours}</div>
      
      {openingHours.closed_on_tuesdays && (
        <div className="text-sm mt-2 text-red-600 font-medium" data-testid="closed-tuesdays">
          ⚠️ Closed on Tuesdays
        </div>
      )}
      
      {openingHours.holiday_hours_notice && (
        <div className="text-sm mt-2 text-amber-600" data-testid="holiday-notice">
          <div className="font-semibold">⚠️ Seasonal Closure</div>
          <div className="mt-1">Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026.</div>
        </div>
      )}
    </div>
  );
};

export default OpeningHours;
